import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, apiSet } from '../utils/api';
import { getToken } from '../utils/auth';

export const CardsContext = createContext();

const emptyData = { main: [], adventures: [], study: [], races: [], reviews: [] };

// Фильтрует прошедшие даты из карточек — то же что делает cron на сервере.
// Применяется при публикации, чтобы стейл-данные в браузере не воскрешали скрытые даты.
function applyDateFilter(data) {
  const now = new Date();
  // МСК = UTC+3
  const msk = new Date(now.getTime() + (3 * 60 - now.getTimezoneOffset()) * 60000);
  const todayIso = msk.toISOString().slice(0, 10);

  const result = { ...data };
  for (const type of ['adventures', 'study', 'races']) {
    if (!Array.isArray(result[type])) continue;
    result[type] = result[type].map(card => {
      if (!card.isPublished) return card;

      if (Array.isArray(card.availableDates) && card.availableDates.length > 0) {
        const future = card.availableDates.filter(slot => {
          const iso = typeof slot === 'object' ? slot.isoDate : null;
          if (!iso) return true;
          return iso > todayIso;
        });
        if (future.length === 0) return { ...card, isPublished: false };
        return { ...card, availableDates: future };
      }

      if (card.startDate && card.startDate <= todayIso) {
        return { ...card, isPublished: false };
      }

      return card;
    });
  }
  return result;
}

export const CardsProvider = ({ children }) => {
  const [cards, setCards]             = useState(emptyData);
  const [productionCards, setProductionCards] = useState(emptyData);
  const [loading, setLoading]         = useState(true);
  const cardsRef = useRef(emptyData);

  // Load from DB on mount
  useEffect(() => {
    const isAdmin = !!getToken();
    const fetches = isAdmin
      ? Promise.all([apiGet('draftCards'), apiGet('productionCards')])
      : apiGet('productionCards').then(prod => [null, prod]);

    fetches
      .then(([draft, prod]) => {
        if (draft) { cardsRef.current = draft; setCards(draft); }
        if (prod)  { setProductionCards(prod); if (!draft) { cardsRef.current = prod; setCards(prod); } }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Persist draft to DB whenever it changes (skip initial empty state)
  const saveDraftToDB = useCallback((data) => {
    cardsRef.current = data;
    apiSet('draftCards', data).catch(console.error);
  }, []);

  // ===== ДОБАВЛЕНИЕ =====
  const addCard = (type, newCard) => {
    setCards(prev => {
      const next = {
        ...prev,
        [type]: [...(prev[type] || []), { ...newCard, id: Date.now(), isPublished: false }],
      };
      saveDraftToDB(next);
      return next;
    });
  };

  // ===== ОБНОВЛЕНИЕ =====
  const updateCard = (type, id, updatedData) => {
    setCards(prev => {
      const next = {
        ...prev,
        [type]: (prev[type] || []).map(c => c.id === id ? { ...c, ...updatedData } : c),
      };
      saveDraftToDB(next);
      return next;
    });
  };

  // ===== ОБНОВЛЕНИЕ С НЕМЕДЛЕННОЙ ПУБЛИКАЦИЕЙ (запись/снятие участника менеджером) =====
  const updateCardLive = (type, id, updatedData) => {
    setCards(prev => {
      const next = {
        ...prev,
        [type]: (prev[type] || []).map(c => c.id === id ? { ...c, ...updatedData } : c),
      };
      saveDraftToDB(next);
      return next;
    });
    setProductionCards(prev => {
      const next = {
        ...prev,
        [type]: (prev[type] || []).map(c => c.id === id ? { ...c, ...updatedData } : c),
      };
      apiSet('productionCards', next).catch(console.error);
      return next;
    });
  };

  // ===== ПЕРЕУПОРЯДОЧИТЬ =====
  const reorderCards = (type, newArray) => {
    setCards(prev => {
      const next = { ...prev, [type]: newArray };
      saveDraftToDB(next);
      return next;
    });
  };

  // ===== УДАЛЕНИЕ =====
  const deleteCard = (type, id) => {
    if (!window.confirm('Удалить эту карточку?')) return;
    setCards(prev => {
      const next = { ...prev, [type]: (prev[type] || []).filter(c => c.id !== id) };
      saveDraftToDB(next);
      return next;
    });
  };

  // ===== ПОКАЗАТЬ / СКРЫТЬ =====
  const togglePublish = (type, id) => {
    setCards(prev => {
      const next = {
        ...prev,
        [type]: (prev[type] || []).map(c => c.id === id ? { ...c, isPublished: !c.isPublished } : c),
      };
      saveDraftToDB(next);
      return next;
    });
  };

  // ===== СОХРАНИТЬ ЧЕРНОВИК =====
  const saveDraft = () => {
    return apiSet('draftCards', cardsRef.current)
      .then(() => alert('✅ Черновик сохранён'))
      .catch(() => alert('❌ Ошибка сохранения'));
  };

  // ===== ОТМЕНИТЬ ИЗМЕНЕНИЯ =====
  const revertAllDrafts = () => {
    cardsRef.current = productionCards;
    setCards(productionCards);
    apiSet('draftCards', productionCards).catch(console.error);
    alert('↩️ Изменения отменены — восстановлено боевое состояние');
    return true;
  };

  // ===== ОПУБЛИКОВАТЬ =====
  const publishToProduction = async () => {
    const filtered = applyDateFilter(cardsRef.current);
    cardsRef.current = filtered;
    setCards(filtered);
    setProductionCards(filtered);
    try {
      await Promise.all([
        apiSet('productionCards', filtered),
        apiSet('draftCards', filtered),
      ]);
      return true;
    } catch {
      alert('❌ Ошибка публикации');
      return false;
    }
  };

  const refreshProductionCards = useCallback(async () => {
    try {
      const prod = await apiGet('productionCards');
      if (prod) setProductionCards(prod);
    } catch { /* ignore */ }
  }, []);

  return (
    <CardsContext.Provider value={{
      cards, productionCards, loading,
      addCard, updateCard, deleteCard, reorderCards,
      togglePublish, saveDraft, revertAllDrafts, publishToProduction, refreshProductionCards, updateCardLive,
    }}>
      {children}
    </CardsContext.Provider>
  );
};
